import { useEffect, useRef, useState } from "react";
import tickSound from "../../../assets/sounds/Countdown-Sound.mp3";

function TickingSoundPlayer({
  countdown,
  playing,
  startTickAt = 300,
  fastTickAt = 30,
}) {
  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  const isTickingRef = useRef(false); // Giữ trạng thái hiện tại
  const [audioEnabled, setAudioEnabled] = useState(false);

  // Initialize audio for mobile compatibility
  useEffect(() => {
    const initAudio = async () => {
      const enableAudio = async () => {
        if (!audioRef.current) {
          audioRef.current = new Audio(tickSound);
          // Mobile-specific settings
          audioRef.current.preload = "auto";
          audioRef.current.volume = 0.8;
          audioRef.current.setAttribute("playsinline", true);
          audioRef.current.setAttribute("webkit-playsinline", true);
        }

        try {
          // Test play for mobile browsers
          const playPromise = audioRef.current.play();
          if (playPromise !== undefined) {
            await playPromise;
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setAudioEnabled(true);
          }
        } catch (error) {
          console.log("Audio not ready yet");
        }
      };

      const handleUserInteraction = () => {
        enableAudio();
        // Remove listeners after first interaction
        document.removeEventListener("touchstart", handleUserInteraction);
        document.removeEventListener("touchend", handleUserInteraction);
        document.removeEventListener("click", handleUserInteraction);
        document.removeEventListener("keydown", handleUserInteraction);
      };

      // Add listeners for mobile and desktop
      document.addEventListener("touchstart", handleUserInteraction, {
        passive: true,
        once: true,
      });
      document.addEventListener("touchend", handleUserInteraction, {
        passive: true,
        once: true,
      });
      document.addEventListener("click", handleUserInteraction, { once: true });
      document.addEventListener("keydown", handleUserInteraction, {
        once: true,
      });

      return () => {
        document.removeEventListener("touchstart", handleUserInteraction);
        document.removeEventListener("touchend", handleUserInteraction);
        document.removeEventListener("click", handleUserInteraction);
        document.removeEventListener("keydown", handleUserInteraction);
      };
    };

    initAudio();
  }, []);

  const playSound = async () => {
    if (!audioRef.current || !audioEnabled) return;

    try {
      audioRef.current.currentTime = 0;
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        await playPromise;
      }
    } catch (error) {
      console.log("Play failed:", error);
    }
  };

  const startSound = () => {
    if (isTickingRef.current || !audioEnabled) return; // đã chạy rồi thì không khởi động lại

    if (!audioRef.current) {
      audioRef.current = new Audio(tickSound);
      // Re-apply mobile settings if audio was recreated
      audioRef.current.preload = "auto";
      audioRef.current.volume = 0.8;
      audioRef.current.setAttribute("playsinline", true);
      audioRef.current.setAttribute("webkit-playsinline", true);
    }

    intervalRef.current = setInterval(
      playSound,
      countdown <= fastTickAt ? 1000 : 1000
    );
    isTickingRef.current = true;
  };

  const stopSound = () => {
    if (!isTickingRef.current) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    isTickingRef.current = false;
  };

  useEffect(() => {
    const shouldTick = playing && countdown <= startTickAt && countdown > 0;

    if (shouldTick) {
      startSound();
    } else {
      stopSound();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, playing, startTickAt, fastTickAt]);

  useEffect(() => {
    return () => stopSound(); // cleanup khi component unmount
  }, []);

  return null;
}

export default TickingSoundPlayer;
